import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { ShieldCheck, Upload, Download, Trash2 } from "lucide-react";
import { API, erreur, nomComplet, telecharger } from "./outils";

const TYPES = {
  vigilance: "Attestation de vigilance URSSAF",
  assurance: "Assurance responsabilité civile",
  insee: "Avis de situation INSEE",
  permis: "Permis de conduire",
  identite: "Pièce d'identité",
  sejour: "Titre de séjour",
  secu: "Attestation de sécurité sociale",
  rib: "RIB",
  medecine: "Visite médicale",
  contrat: "Contrat signé",
};
// Les pieces exigibles dependent du statut : la vigilance URSSAF n'existe que
// pour un independant, la securite sociale ne concerne que le salariat.
const SALARIE = ["identite", "sejour", "secu", "rib", "permis", "medecine"];
const PAR_STATUT = {
  auto_entrepreneur: ["vigilance", "assurance", "insee", "identite", "permis", "rib"],
  etudiant: SALARIE, interim: SALARIE, cdd_usage: SALARIE, autre: SALARIE,
};

export default function DocumentsVivier({ personne, peutDeposer = true, libelle = "Pièces" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState([]);
  const dispo = PAR_STATUT[personne.statut] || PAR_STATUT.autre;
  const [type, setType] = useState(dispo[0]);
  const [emission, setEmission] = useState(new Date().toISOString().split("T")[0]);
  const [expiration, setExpiration] = useState("");

  const charger = async () => {
    try {
      const r = await axios.get(`${API}/vivier/${personne.id}/documents`);
      setDocs(r.data || []);
    } catch (e) {
      setDocs([]);
    }
  };

  useEffect(() => { if (open) charger(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const deposer = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 4000000) return toast.error("Fichier trop lourd (4 Mo maximum)");
    const lecteur = new FileReader();
    lecteur.onload = async () => {
      try {
        await axios.post(`${API}/vivier/${personne.id}/documents`, {
          type, fichier: f.name, contenu: String(lecteur.result),
          date_emission: emission, date_expiration: expiration,
        });
        toast.success("Pièce déposée");
        setExpiration("");
        charger();
      } catch (err) {
        toast.error(erreur(err, "Dépôt impossible"));
      }
    };
    lecteur.readAsDataURL(f);
  };

  const supprimer = async (d) => {
    try {
      await axios.delete(`${API}/vivier/${personne.id}/documents/${d.id}`);
      charger();
    } catch (e) {
      toast.error(erreur(e, "Suppression impossible"));
    }
  };

  const etat = (d) => {
    if (!d.date_expiration) return { txt: "Sans échéance", cls: "text-muted-foreground" };
    const jours = Math.ceil((new Date(d.date_expiration) - new Date()) / 86400000);
    if (jours < 0) return { txt: `Expirée depuis ${-jours} j`, cls: "text-red-600 font-medium" };
    if (jours <= 30) return { txt: `Expire dans ${jours} j`, cls: "text-amber-600 font-medium" };
    return { txt: `Valide jusqu'au ${new Date(d.date_expiration).toLocaleDateString("fr-FR")}`, cls: "text-emerald-700" };
  };

  return (
    <>
      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setOpen(true)}>
        <ShieldCheck className="w-3.5 h-3.5 mr-1" /> {libelle}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pièces justificatives : {nomComplet(personne)}</DialogTitle>
          </DialogHeader>

          {peutDeposer && (
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end border-b pb-4">
              <div className="sm:col-span-1">
                <Label className="text-xs">Type de pièce</Label>
                <select className="w-full h-9 border rounded-md px-2 text-sm" value={type}
                  onChange={(e) => setType(e.target.value)}>
                  {dispo.map((k) => <option key={k} value={k}>{TYPES[k]}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Date d'émission</Label>
                <Input type="date" value={emission} onChange={(e) => setEmission(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Expire le (si indiqué)</Label>
                <Input type="date" value={expiration} onChange={(e) => setExpiration(e.target.value)} />
              </div>
              <div>
                <label htmlFor={`depot-${personne.id}`}
                  className="flex items-center justify-center h-9 w-full rounded-md border bg-secondary/10 text-sm font-medium cursor-pointer hover:bg-secondary/20">
                  <Upload className="h-4 w-4 mr-1" /> Déposer
                </label>
                <input id={`depot-${personne.id}`} type="file" className="hidden"
                  accept="application/pdf,image/jpeg,image/png" onChange={deposer} />
              </div>
            </div>
          )}

          <div className="space-y-2 pt-2">
            {docs.length === 0 && (
              <p className="text-sm text-muted-foreground py-6 text-center">Aucune pièce disponible.</p>
            )}
            {docs.map((d) => {
              const e = etat(d);
              return (
                <div key={d.id} className="flex items-center justify-between border rounded-md p-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{TYPES[d.type] || d.type}</p>
                    <p className="text-xs text-muted-foreground truncate">{d.fichier}</p>
                    <p className={`text-xs mt-0.5 ${e.cls}`}>{e.txt}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon"
                      onClick={() => telecharger(`/vivier/${personne.id}/documents/${d.id}`, d.fichier)}>
                      <Download className="h-4 w-4" />
                    </Button>
                    {user?.role === "admin" && (
                      <Button variant="ghost" size="icon" onClick={() => supprimer(d)}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
